import React, { memo } from 'react'
import EndorsementsModal from 'transfix-form/forms/CarrierFleetInformation/components/EndorsementsModal/EndorsementsModal'
import EquipmentTypesModal from 'transfix-form/forms/CarrierFleetInformation/components/EquipmentTypesModal/EquipmentTypesModal'
import FreightCapabilityModal from 'transfix-form/forms/CarrierFleetInformation/components/FreightCapabilityModal/FreightCapabilityModal'
import {
  getEndorsementsDefaultValues,
  getEndorsementsLabels,
  getEquipmentTypesDefaultValues,
  getEquipmentTypesLabels,
  getFreightCapabilitiesDefaultValues,
  getFreightCapabilitiesLabels,
} from 'transfix-form/forms/CarrierFleetInformation/utils'
import Card from 'transfix-ui/components/Card'
import Typography from 'transfix-ui/core/Typography'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useModalState } from 'utils/hooks'
import CarrierProfileCardRow from '../CarrierProfileCardRow'
import { IFleetInfoFragment } from './graphql/FleetInfoFragment'
import {
  useFreightCapabilitySubmitter,
  useEquipmentTypesSubmitter,
  useEndorsementsSubmitter,
} from './hooks'

export interface ICarrierFleetInfoCardProps extends IFleetInfoFragment {}

const CarrierFleetInfoCard = memo<ICarrierFleetInfoCardProps>(
  ({ endorsements, equipmentCapability, freightCapability, id: motorCarrierId }) => {
    const { trackEvent } = useFMSAnalytics()

    const { createToggleHandler, modalState } = useModalState({
      freightCapabilityModal: false,
      equipmentTypesModal: false,
      endorsementsModal: false,
    })

    const {
      endorsementsModal: isEndorsementsModalOpen,
      equipmentTypesModal: isEquipmentTypesModalOpen,
      freightCapabilityModal: isFreightCapabilityModalOpen,
    } = modalState || {}

    const toggleFreightCapabilityModalHandler = createToggleHandler('freightCapabilityModal')
    const toggleEquipmentTypesModalHandler = createToggleHandler('equipmentTypesModal')
    const toggleEndorsementsModalHandler = createToggleHandler('endorsementsModal')

    /**
     * Freight Capabilities
     */
    const openFreightCapabilityModal = () => {
      trackEvent('Carrier Profile', 'OPENED_FREIGHT_CAPABILITIES')
      toggleFreightCapabilityModalHandler()
    }
    const freightCapabilitiesLabels = getFreightCapabilitiesLabels(freightCapability)
    const freightCapabilitiesDefaultValues = getFreightCapabilitiesDefaultValues(freightCapability)
    const {
      loading: freightCapabilitiesLoading,
      submitHandler: freightCapabilitiesSubmitHandler,
    } = useFreightCapabilitySubmitter(motorCarrierId)

    /**
     * Equipment Types
     */
    const openEquipmentTypesModal = () => {
      trackEvent('Carrier Profile', 'OPENED_EQUIPMENT_TYPES')
      toggleEquipmentTypesModalHandler()
    }
    const equipmentTypesLabels = getEquipmentTypesLabels(equipmentCapability)
    const { preferenceLabel: equipmentTypesPreferenceLabels, subLabel: equipmentSubLabel } =
      equipmentTypesLabels || {}
    const equipmentTypesDefaultValues = getEquipmentTypesDefaultValues(equipmentCapability)
    const {
      loading: equipmentTypesLoading,
      submitHandler: equipmentTypesSubmitHandler,
    } = useEquipmentTypesSubmitter(motorCarrierId)

    /**
     * Endorsements
     */
    const openEndorsementsModal = () => {
      trackEvent('Carrier Profile', 'OPENED_ENDORSEMENTS')
      toggleEndorsementsModalHandler()
    }
    const endorsementsLabels = getEndorsementsLabels(endorsements)
    const endorsementsDefaultValues = getEndorsementsDefaultValues(endorsements)
    const {
      loading: endorsementsLoading,
      submitHandler: endorsementsSubmitHandler,
    } = useEndorsementsSubmitter(motorCarrierId)

    return (
      <Card>
        <Typography variant='h3' align='left'>
          Fleet Info
        </Typography>
        <Typography variant='body2' align='left'>
          Based on the equipment and capabilities you have, we&apos;ll be able to give you the best
          load recommendations.
        </Typography>
        <CarrierProfileCardRow
          title='Freight capabilities'
          value={
            freightCapabilitiesLabels ? (
              <Typography component='div' variant='body1'>
                {freightCapabilitiesLabels}
              </Typography>
            ) : null
          }
          onEdit={openFreightCapabilityModal}
        />
        <CarrierProfileCardRow
          title='Equipment types'
          value={
            equipmentTypesLabels ? (
              <>
                <Typography component='div' variant='body1'>
                  {equipmentTypesPreferenceLabels}
                </Typography>
                <Typography component='div' variant='body1' color='textSecondary'>
                  {equipmentSubLabel}
                </Typography>
              </>
            ) : null
          }
          onEdit={openEquipmentTypesModal}
        />
        <CarrierProfileCardRow
          title='Endorsements'
          showBorder={false}
          value={
            endorsementsLabels ? (
              <Typography component='div' variant='body1'>
                {endorsementsLabels}
              </Typography>
            ) : null
          }
          onEdit={openEndorsementsModal}
        />
        {isFreightCapabilityModalOpen && (
          <FreightCapabilityModal
            defaultValues={freightCapabilitiesDefaultValues}
            loading={freightCapabilitiesLoading}
            onClose={toggleFreightCapabilityModalHandler}
            onSubmit={freightCapabilitiesSubmitHandler}
          />
        )}
        {isEquipmentTypesModalOpen && (
          <EquipmentTypesModal
            defaultValues={equipmentTypesDefaultValues}
            loading={equipmentTypesLoading}
            onClose={toggleEquipmentTypesModalHandler}
            onSumbit={equipmentTypesSubmitHandler}
          />
        )}
        {isEndorsementsModalOpen && (
          <EndorsementsModal
            defaultValues={endorsementsDefaultValues}
            loading={endorsementsLoading}
            onClose={toggleEndorsementsModalHandler}
            onSubmit={endorsementsSubmitHandler}
          />
        )}
      </Card>
    )
  }
)

export default CarrierFleetInfoCard
